
import React, { useState, useEffect, useCallback, useMemo } from "react";
import SearchBar from "../components/CommonComponents/SearchInput";
import { useTheme } from "../components/CommonComponents/ThemeProvider";
import EmptyState from "../components/CommonComponents/EmptyState";
// import ViewToggle from "../components/CommonComponents/ViewToggle";
import axios from "axios";
import DeleteModal from "../components/CommonComponents/ConfirmationModal";
import AddUserModal from "../components/admin/AddUserModal";
import { showSuccessToast, showErrorToast } from "../utils/toast";
import UserTable from '../components/admin/users/UserTable';
import UserProfilePreviewModal from "../components/admin/users/UserProfilePreviewModal";
import { CreditCard, Minus, Plus, Users } from 'lucide-react';
import { fetchBranches } from "../services/branchApi";
import { createAddonRequest, fetchAddonSummary } from "../services/addonRequestApi";

const ROLES_TO_EXCLUDE = ["Admin", "SuperAdmin"];
const DEFAULT_SUBSCRIPTION_SETTINGS = {
  base_monthly_amount: 1699,
  additional_user_monthly_amount: 150,
  included_users_per_active_branch: 2,
};

export default function UserListPage() {
  useTheme();
  const [refreshFlag, setRefreshFlag] = useState(false);
  // ===================
  // State
  // ===================
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [branches, setBranches] = useState([]);
  const [addonSummary, setAddonSummary] = useState({
    approvedAdditionalUsers: 0,
    pendingAdditionalUsers: 0,
    approvedAddonMonthlyTotal: 0,
    subscriptionSettings: DEFAULT_SUBSCRIPTION_SETTINGS,
    latestRequests: [],
  });
  const [addonSubmitting, setAddonSubmitting] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [noActiveBranchModalOpen, setNoActiveBranchModalOpen] = useState(false);
  const [addonQuantity, setAddonQuantity] = useState(1);
  const [modalState, setModalState] = useState({
    type: null,
    user: null,
    open: false,
  });
  const [previewUser, setPreviewUser] = useState(null);

  const usersPerPage = 5;

  // ===================
  // Current logged-in user
  // ===================
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user")) || {};
    } catch {
      return {};
    }
  }, []);

  const baseURL = `${process.env.REACT_APP_API_URL}/api/admin/users`;

  // ===================
  // Load users
  // ===================
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      const res = await axios.get(baseURL, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(res.data.data || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [baseURL]);

  const loadBranches = useCallback(async () => {
    try {
      const response = await fetchBranches();
      const branchList = response.data?.data || response.data || [];
      setBranches(Array.isArray(branchList) ? branchList : []);
    } catch (err) {
      console.error("Error fetching branches:", err);
      setBranches([]);
    }
  }, []);

  const loadAddonSummary = useCallback(async () => {
    try {
      const response = await fetchAddonSummary();
      setAddonSummary({
        approvedAdditionalUsers: Number(response.data?.approvedAdditionalUsers || 0),
        pendingAdditionalUsers: Number(response.data?.pendingAdditionalUsers || 0),
        approvedAddonMonthlyTotal: Number(response.data?.approvedAddonMonthlyTotal || 0),
        subscriptionSettings: response.data?.subscriptionSettings || DEFAULT_SUBSCRIPTION_SETTINGS,
        latestRequests: response.data?.latestRequests || [],
      });
    } catch (err) {
      console.error("Error fetching add-on summary:", err);
      setAddonSummary({
        approvedAdditionalUsers: 0,
        pendingAdditionalUsers: 0,
        approvedAddonMonthlyTotal: 0,
        subscriptionSettings: DEFAULT_SUBSCRIPTION_SETTINGS,
        latestRequests: [],
      });
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers, refreshFlag]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadAddonSummary();
  }, [loadAddonSummary, refreshFlag]);

  // ===================
  // Filter & paginate
  // ===================
  const filteredUsers = useMemo(() => users.filter((user) => {
    const matchesCustomer = user.customer_id === currentUser.customer_id;
    const roleExcluded = ROLES_TO_EXCLUDE.includes(user.role);
    const matchesSearch = user.username
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchesCustomer && !roleExcluded && matchesSearch;
  }), [currentUser.customer_id, searchTerm, users]);

  const activeBranchCount = useMemo(
    () => branches.filter((branch) => String(branch.status || "").toUpperCase() === "ACTIVE").length,
    [branches]
  );
  const subscriptionSettings = addonSummary.subscriptionSettings || DEFAULT_SUBSCRIPTION_SETTINGS;
  const includedUsersPerActiveBranch = Number(subscriptionSettings.included_users_per_active_branch || DEFAULT_SUBSCRIPTION_SETTINGS.included_users_per_active_branch);
  const baseMonthlySubscription = Number(subscriptionSettings.base_monthly_amount || DEFAULT_SUBSCRIPTION_SETTINGS.base_monthly_amount);
  const additionalUserMonthlyPrice = Number(subscriptionSettings.additional_user_monthly_amount || DEFAULT_SUBSCRIPTION_SETTINGS.additional_user_monthly_amount);
  const includedUserLimit = activeBranchCount * includedUsersPerActiveBranch;
  const approvedAdditionalUsers = Number(addonSummary.approvedAdditionalUsers || 0);
  const pendingAdditionalUsers = Number(addonSummary.pendingAdditionalUsers || 0);
  const approvedAddonMonthlyTotal = Number(addonSummary.approvedAddonMonthlyTotal || 0);
  const totalUserLimit = includedUserLimit + approvedAdditionalUsers;
  const usedStaffCount = useMemo(
    () => users.filter((user) =>
      user.customer_id === currentUser.customer_id
      && !ROLES_TO_EXCLUDE.includes(user.role)
      && String(user.status || "").toUpperCase() !== "DELETED"
    ).length,
    [currentUser.customer_id, users]
  );
  const remainingIncludedUsers = Math.max(totalUserLimit - usedStaffCount, 0);
  const isUserLimitReached = usedStaffCount >= totalUserLimit;
  const currentMonthlyTotal = baseMonthlySubscription + approvedAddonMonthlyTotal;
  const normalizedAddonQuantity = Math.max(Number(addonQuantity) || 1, 1);
  const addonMonthlyTotal = additionalUserMonthlyPrice * normalizedAddonQuantity;
  const upgradedMonthlyTotal = currentMonthlyTotal + addonMonthlyTotal;

  const totalPages = Math.max(Math.ceil(filteredUsers.length / usersPerPage), 1);
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredUsers, currentPage, totalPages]);

  // ===================
  // Modal handlers
  // ===================
  const closeModal = () =>
    setModalState({ type: null, user: null, open: false });
  const handleAddUser = () => {
    if (activeBranchCount === 0) {
      setNoActiveBranchModalOpen(true);
      return;
    }
    if (isUserLimitReached) {
      setAddonQuantity(1);
      setUpgradeModalOpen(true);
      return;
    }
    setSearchTerm("");
    setCurrentPage(1);
    setModalState({ type: "add", user: null, open: true });
  };
  const handleEditUser = (user) =>
    setModalState({ type: "edit", user, open: true });

  const handleOpenAddonRequest = () => {
    if (activeBranchCount === 0) {
      setNoActiveBranchModalOpen(true);
      return;
    }
    setAddonQuantity(1);
    setUpgradeModalOpen(true);
  };

  const handleUserAddedOrUpdated = () => {
    loadUsers();
    loadAddonSummary();
    closeModal();
    setRefreshFlag(prev => !prev);
  };

  const handleRequestAddon = async () => {
    setAddonSubmitting(true);
    try {
      const response = await createAddonRequest({ quantity: normalizedAddonQuantity });
      const message = response.data?.emailSent
        ? response.data?.requesterCopySent
          ? "Add-on request sent. A copy was emailed to you."
          : "Add-on request sent. ZeroOne will review it."
        : "Add-on request saved. Email notification needs Microsoft Graph setup.";
      showSuccessToast(message);
      setUpgradeModalOpen(false);
      await loadAddonSummary();
    } catch (error) {
      const status = error.response?.status;
      const message = error.response?.data?.message || "Failed to request add-on.";
      if (status === 409) {
        showErrorToast("You already have a pending add-on request.");
        await loadAddonSummary();
      } else {
        showErrorToast(message);
      }
    } finally {
      setAddonSubmitting(false);
    }
  };

  const handleAddonQuantityChange = (value) => {
    const nextValue = Math.max(parseInt(value || "1", 10) || 1, 1);
    setAddonQuantity(nextValue);
  };

  const handleConfirmDelete = async () => {
    if (!modalState.user) return;
    try {
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      await axios.put(
        `${baseURL}/${modalState.user.id}/status`,
        { status: "DELETED" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === modalState.user.id ? { ...u, status: "DELETED" } : u
        )
      );
      closeModal();
      showSuccessToast("User deleted successfully.");
    } catch (error) {
      console.error("Error deleting user:", error);
      showErrorToast("Failed to delete user.");
    }
  };

  const handleToggleUserStatus = async () => {
    if (!modalState.user) return;
    try {
      const newStatus =
        modalState.user.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
      const token = sessionStorage.getItem("token") || localStorage.getItem("token");
      await axios.put(
        `${baseURL}/${modalState.user.id}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === modalState.user.id ? { ...u, status: newStatus } : u
        )
      );
      closeModal();
      showSuccessToast(`User status set to ${newStatus}.`);
    } catch (error) {
      console.error("Error toggling status:", error);
      showErrorToast("Failed to update user status.");
    }
  };

  // ===================
  // JSX
  // ===================
  return (
    <div className="flex flex-col gap-4 pt-3">
      {error && (
        <EmptyState
          icon={Users}
          title="Failed to Load Users"
          message="Check your internet connection and try again."
          onRetry={loadUsers}
          retryLabel="Retry"
        />
      )}
      {!error && (
        <>
          {/* Search & Add */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <SearchBar
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              disabled={loading}
            />
            <div className="grid w-full min-w-0 grid-cols-1 gap-2 sm:grid-cols-2 md:w-auto md:min-w-[420px]">
              <button
                type="button"
                onClick={handleOpenAddonRequest}
                disabled={loading}
                className="inline-flex w-full min-w-0 items-center justify-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                <CreditCard className="h-4 w-4" />
                Request Add User
              </button>
              <button
                type="button"
                onClick={handleAddUser}
                disabled={loading}
                className="inline-flex w-full min-w-0 items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ background: "var(--color-primary)" }}
              >
                + Add User
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Included Staff Rule</p>
                <p className="mt-1 text-sm text-slate-500">
                  Your subscription includes <span className="font-semibold text-slate-700">{includedUsersPerActiveBranch} cashier/staff accounts per active branch</span>.
                  Active branches: <span className="font-semibold text-slate-700">{activeBranchCount}</span>.
                  Approved add-ons: <span className="font-semibold text-slate-700">{approvedAdditionalUsers}</span>.
                  Pending requests: <span className="font-semibold text-slate-700">{pendingAdditionalUsers}</span>.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-xs sm:min-w-[360px]">
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="font-semibold text-slate-900">{totalUserLimit}</p>
                  <p className="text-slate-500">Allowed Slots</p>
                </div>
                <div className="rounded-xl bg-slate-50 px-3 py-2">
                  <p className="font-semibold text-slate-900">{usedStaffCount}</p>
                  <p className="text-slate-500">Used</p>
                </div>
                <div className={`rounded-xl px-3 py-2 ${isUserLimitReached ? "bg-rose-50" : "bg-emerald-50"}`}>
                  <p className={`font-semibold ${isUserLimitReached ? "text-rose-700" : "text-emerald-700"}`}>{remainingIncludedUsers}</p>
                  <p className={isUserLimitReached ? "text-rose-500" : "text-emerald-600"}>Remaining</p>
                </div>
              </div>
            </div>
          </div>

          {/* Table Header */}
          <div className="rounded-[24px] border border-slate-200 bg-white px-5 py-4 shadow-[0_14px_35px_rgba(15,23,42,0.05)]">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900">Staff Accounts</p>
                <p className="mt-1 text-sm text-slate-500">Manage cashier and staff access for active branches.</p>
              </div>
              <div className="inline-flex w-fit items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-500">
                {filteredUsers.length} total
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            {/* Users Table */}
            <UserTable
              users={currentUsers}
              onPreviewProfile={setPreviewUser}
              onEdit={handleEditUser}
              onDelete={user => setModalState({ type: "delete", user, open: true })}
              onToggleStatus={user => setModalState({ type: "toggle", user, open: true })}
              loading={loading}
            />

            {/* Pagination */}
            <div className="flex items-center justify-between gap-3 overflow-x-auto pb-10 text-xs whitespace-nowrap">
              <div className="shrink-0 text-gray-500">
                Showing {filteredUsers.length === 0 ? 0 : indexOfFirstUser + 1}
                -{Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <div className="text-xs text-gray-600">
                  Page {currentPage} of {totalPages}
                </div>
                <button
                  type="button"
                  className="px-3 py-1.5 rounded-lg border text-xs disabled:opacity-50"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {modalState.open && (modalState.type === "add" || modalState.type === "edit") && (
        <AddUserModal
          editData={modalState.type === "edit" ? modalState.user : null}
          onClose={closeModal}
          onUserAddedOrUpdated={handleUserAddedOrUpdated}
        />
      )}
      {modalState.open && modalState.type === "delete" && (
        <DeleteModal
          isOpen
          onClose={closeModal}
          onConfirm={handleConfirmDelete}
          title="Delete User"
          message={`Are you sure you want to delete "${modalState.user?.username}"?`}
          confirmText="Yes"
          cancelText="No"
          type="delete"
        />
      )}
      {modalState.open && modalState.type === "toggle" && (
        <DeleteModal
          isOpen
          onClose={closeModal}
          onConfirm={handleToggleUserStatus}
          title="Change User Status"
          message={`Are you sure you want to set this user to ${modalState.user?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"}?`}
          confirmText="Yes"
          cancelText="No"
          type="delete"
        />
      )}
      <UserProfilePreviewModal
        open={Boolean(previewUser)}
        onClose={() => setPreviewUser(null)}
        user={previewUser}
      />

      <DeleteModal
        isOpen={noActiveBranchModalOpen}
        onClose={() => setNoActiveBranchModalOpen(false)}
        onConfirm={() => setNoActiveBranchModalOpen(false)}
        title="No Active Branch"
        message="Activate or create at least one branch before adding staff accounts. Add-on slots are counted per active branch, so they cannot be used while all branches are inactive."
        confirmText="Got it"
        cancelText="Close"
        type="default"
      />

      {upgradeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start gap-4">
              <div
                className="inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm"
                style={{ background: "var(--color-primary)" }}
              >
                <CreditCard className="h-6 w-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-xl font-semibold text-slate-900">Request Extra Staff Account</h2>
                <p className="mt-2 text-sm text-slate-500">
                  {isUserLimitReached
                    ? `You have reached the current limit of ${totalUserLimit} staff account${totalUserLimit === 1 ? "" : "s"} for ${activeBranchCount} active branch${activeBranchCount === 1 ? "" : "es"}.`
                    : `You still have ${remainingIncludedUsers} staff slot${remainingIncludedUsers === 1 ? "" : "s"} available, but you can request more slots in advance.`}
                  {pendingAdditionalUsers > 0 ? " You already have a pending add-on request waiting for ZeroOne review." : ""}
                </p>
              </div>
            </div>
            <div
              className="mt-5 rounded-2xl border bg-slate-50 p-4"
              style={{
                borderColor: "color-mix(in srgb, var(--color-primary) 18%, #e2e8f0)",
                background: "linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 6%, white), #f8fafc)",
              }}
            >
              <div className="mb-4 flex flex-col gap-2 border-b border-slate-200 pb-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">Additional staff slots</p>
                  <p className="text-xs text-slate-500">Choose how many accounts to request.</p>
                </div>
                <div
                  className="flex h-11 items-center rounded-lg border bg-white shadow-sm"
                  style={{ borderColor: "color-mix(in srgb, var(--color-primary) 18%, #e2e8f0)" }}
                >
                  <button
                    type="button"
                    onClick={() => handleAddonQuantityChange(normalizedAddonQuantity - 1)}
                    disabled={normalizedAddonQuantity <= 1 || addonSubmitting}
                    className="flex h-full w-10 items-center justify-center text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-40"
                    onMouseOver={(event) => {
                      event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 10%, white)";
                      event.currentTarget.style.color = "var(--color-primary)";
                    }}
                    onMouseOut={(event) => {
                      event.currentTarget.style.background = "transparent";
                      event.currentTarget.style.color = "";
                    }}
                    aria-label="Decrease additional staff slots"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={addonQuantity}
                    onChange={(event) => handleAddonQuantityChange(event.target.value)}
                    disabled={addonSubmitting}
                    className="no-number-spinner h-full w-16 border-x border-slate-200 text-center text-sm font-semibold text-slate-900 outline-none focus:bg-white"
                    style={{ borderColor: "color-mix(in srgb, var(--color-primary) 18%, #e2e8f0)" }}
                    aria-label="Additional staff slot quantity"
                  />
                  <button
                    type="button"
                    onClick={() => handleAddonQuantityChange(normalizedAddonQuantity + 1)}
                    disabled={addonSubmitting}
                    className="flex h-full w-10 items-center justify-center text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-40"
                    onMouseOver={(event) => {
                      event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 10%, white)";
                      event.currentTarget.style.color = "var(--color-primary)";
                    }}
                    onMouseOut={(event) => {
                      event.currentTarget.style.background = "transparent";
                      event.currentTarget.style.color = "";
                    }}
                    aria-label="Increase additional staff slots"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">Current monthly subscription</span>
                <span className="font-semibold text-slate-900">₱{currentMonthlyTotal.toLocaleString()}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-slate-500">
                  Additional user{normalizedAddonQuantity === 1 ? "" : "s"} ({normalizedAddonQuantity} × ₱{additionalUserMonthlyPrice.toLocaleString()})
                </span>
                <span className="font-semibold text-slate-900">+ ₱{addonMonthlyTotal.toLocaleString()}/month</span>
              </div>
              <div className="mt-3 border-t border-slate-200 pt-3 flex items-center justify-between">
                <span className="text-sm font-semibold text-slate-700">New monthly billed amount</span>
                <span className="text-lg font-bold" style={{ color: "var(--color-primary)" }}>₱{upgradedMonthlyTotal.toLocaleString()}</span>
              </div>
            </div>
            <p className="mt-3 text-xs text-slate-500">
              This request adds {normalizedAddonQuantity} extra staff account allowance{normalizedAddonQuantity === 1 ? "" : "s"} to the monthly subscription.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setUpgradeModalOpen(false)}
                className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleRequestAddon}
                disabled={addonSubmitting || pendingAdditionalUsers > 0}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition disabled:cursor-not-allowed disabled:bg-slate-400"
                style={addonSubmitting || pendingAdditionalUsers > 0 ? undefined : { background: "var(--color-primary)" }}
                onMouseOver={addonSubmitting || pendingAdditionalUsers > 0 ? undefined : (event) => {
                  event.currentTarget.style.background = "color-mix(in srgb, var(--color-primary) 82%, black)";
                }}
                onMouseOut={addonSubmitting || pendingAdditionalUsers > 0 ? undefined : (event) => {
                  event.currentTarget.style.background = "var(--color-primary)";
                }}
              >
                {addonSubmitting ? "Sending..." : pendingAdditionalUsers > 0 ? "Request Pending" : "Request Add-on"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
